import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { SiteShell } from "@/components/marketing/site-shell";
import { supabase } from "@/integrations/supabase/client";
import { IMPLEMENTATION_PACKAGES } from "@/lib/implementation-packages";

const TITLE = "Implementation services — AROS FLOW";
const DESC =
  "ArosFlow done-with-you implementation: $2,500 Operator or $5,000 Acquisition Team one-time. Workflow setup, underwriting calibration, buy boxes and training. Software terms quoted separately.";

export const Route = createFileRoute("/implementation")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ImplementationPage,
});

const OUTCOMES = [
  "Consistent review of underwriting assumptions",
  "A repeatable lead-to-offer workflow",
  "Clearer handoffs instead of scattered spreadsheets",
  "Visible comps, sources and editable assumptions",
  "Offer guardrails your team can review",
  "A written process for acquisitions-rep onboarding",
];

function ImplementationPage() {
  return (
    <SiteShell>
      <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        <h1 className="text-3xl font-semibold tracking-tight">
          Turn your acquisition process into a repeatable lead-to-offer workflow.
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Done-with-you ArosFlow implementation, configured around how your operation actually
          evaluates leads. These are one-time professional service fees, separate from software
          access.
        </p>

        <ul className="mt-6 grid gap-2 text-sm sm:grid-cols-2">
          {OUTCOMES.map((o) => (
            <li key={o} className="flex gap-2 text-white/85">
              <span className="text-gold" aria-hidden>
                ·
              </span>
              {o}
            </li>
          ))}
        </ul>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {IMPLEMENTATION_PACKAGES.map((p) => (
            <div key={p.id} className="rounded-2xl border border-[var(--glass-border)] bg-card p-6">
              <h2 className="text-sm font-semibold">{p.name}</h2>
              <div className="mt-1 text-3xl font-semibold">
                {p.price}
                <span className="text-sm font-normal text-muted-foreground"> one-time</span>
              </div>
              <ul className="mt-4 space-y-1.5 text-xs text-white/85">
                {p.items.map((i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-gold" aria-hidden>
                      ·
                    </span>
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-6 text-xs text-muted-foreground">
          Not included: multi-user team seats, blast messaging, automated texting, call copilot and
          any other Early Access or in-development feature. Implementation covers only what ArosFlow
          does in production today. We make no promise of revenue, closings or time savings.
        </p>

        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Software access, usage limits, and any ongoing charges are specified in your written
          proposal before you commit; implementation fees do not include lifetime access.
        </p>

        <RequestForm />
      </div>
    </SiteShell>
  );
}

function RequestForm() {
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");
  const [err, setErr] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? "").trim();
    if (!v("company") || !v("name") || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v("email"))) {
      setErr("Company, name and a valid email are required.");
      setState("error");
      return;
    }
    setState("busy");
    const { error } = await supabase.from("implementation_requests").insert({
      package: (v("package") || "unsure") as "operator" | "team" | "unsure",
      company: v("company").slice(0, 200),
      name: v("name").slice(0, 120),
      email: v("email").slice(0, 254),
      phone: v("phone").slice(0, 40) || null,
      monthly_lead_volume: v("volume").slice(0, 60) || null,
      current_tools: v("tools").slice(0, 1000) || null,
    });
    if (error) {
      setErr("We couldn't send that. Please try again in a moment.");
      setState("error");
      return;
    }
    setState("done");
  };

  if (state === "done") {
    return (
      <div className="mt-10 rounded-2xl border border-gold/40 bg-card p-6 text-sm">
        Thanks — your request is in. We'll reply by email to schedule the call.
      </div>
    );
  }

  const input =
    "focus-key w-full rounded-lg border border-[var(--glass-border)] bg-background px-3 py-2 text-sm";
  return (
    <form
      onSubmit={onSubmit}
      id="request"
      className="mt-10 grid gap-3 rounded-2xl border border-[var(--glass-border)] bg-card p-6 sm:grid-cols-2"
    >
      <h2 className="text-lg font-semibold sm:col-span-2">Request an Implementation Call</h2>
      <input name="company" aria-label="Company" placeholder="Company *" className={input} />
      <input name="name" aria-label="Your name" placeholder="Your name *" className={input} />
      <input name="email" type="email" aria-label="Email" placeholder="Email *" className={input} />
      <input name="phone" aria-label="Phone" placeholder="Phone (optional)" className={input} />
      <input
        name="volume"
        aria-label="Monthly lead volume"
        placeholder="Monthly lead volume"
        className={input}
      />
      <select name="package" aria-label="Package" className={input} defaultValue="unsure">
        <option value="unsure">Not sure yet</option>
        <option value="operator">Operator Implementation ($2,500)</option>
        <option value="team">Acquisition Team Implementation ($5,000)</option>
      </select>
      <textarea
        name="tools"
        aria-label="Current underwriting tools"
        placeholder="Current underwriting tools (spreadsheets, DealCheck, etc.)"
        className={`${input} sm:col-span-2`}
        rows={3}
      />
      {state === "error" && <p className="text-xs text-destructive sm:col-span-2">{err}</p>}
      <button
        type="submit"
        disabled={state === "busy"}
        className="focus-key rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-60 sm:col-span-2"
      >
        {state === "busy" ? "Sending…" : "Request an Implementation Call"}
      </button>
    </form>
  );
}
